import jwt_decode from 'jwt-decode';

import { AuthError } from 'models/authError';

import { IAuthenticationActionCreators, UserState } from 'store/redux/Authentication';
import * as AuthenticationStore from 'store/redux/Authentication';
import { IUserInRole } from 'pages/Login';
import { IRefreshedAccessTokenResult } from './authenticationService';
import { SsoInfo } from 'clients/contracts';
import { ApiError } from 'clients/ApiClient';

const ThrowAuthErrorOnErrorResponse = (response: Response) => {
  if (response.status === 400) {
    throw new AuthError('400', 'Bad request');
  } else if (response.status === 401) {
    throw new AuthError('401', 'Unauthorized');
  } else if (response.status === 403) {
    throw new AuthError('403', 'Forbidden');
  }
  if (response.status === 404) {
    throw new AuthError('404', 'Not found');
  } else if (response.status < 200 || response.status > 400) {
    throw new AuthError(response.status.toString(), 'Server error');
  } else {
    return response;
  }
};

const ThrowApiErrorOnErrorResponse = async (response: Response) => {
  if (response.ok) return response;
  throw new ApiError(response.status, await response.text());
};

const HandleGetLimitedUserInformationResponse = async (response: Response) => {
  if (response.ok) return response;

  if (response.status === 403) {
    throw (await response.json()) as UserRegistrationStateError;
  }

  return response;
};

const HandleCreateNewUserResponse = async (response: Response) => {
  const createNewUserErrorCodes = [400, 403, 409];

  if (response.ok) return response;
  if (createNewUserErrorCodes.includes(response.status))
    throw (await response.json()) as UserRegistrationStateError;

  return response;
};

const CatchAndThrowFailedToGetInfo = (error: any) => {
  if (error.message && error.message === 'Failed to get info') {
    throw new AuthError('-1', 'Could not find server');
  } else {
    throw error;
  }
};

const DoNothing = () => {};

var refreshTokenPromise: Promise<string> | null;

const Create = (
  APIBaseUrl: string,
  actionCreators: IAuthenticationActionCreators,
  currentUserState: UserState | undefined
): ICustomLoginService => {
  const fetchUserDetails = (nativeAccessToken: string) => {
    let url: string = APIBaseUrl + '/user/customer';
    if (currentUserState?.RequestedCustomerId) {
      url += '?specificCustomerId=' + currentUserState?.RequestedCustomerId;
    }
    return fetch(url, {
      method: 'GET',
      mode: 'cors', // no-cors, *cors, same-origin
      cache: 'no-cache',
      credentials: 'same-origin', // include, *same-origin, omit
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'bearer ' + nativeAccessToken,
      },
      redirect: 'error',
      referrerPolicy: 'no-referrer',
    })
      .then(ThrowAuthErrorOnErrorResponse)
      .then((response: Response) => response.json())
      .then((responseAsJson: IUserInRole) => {
        if (responseAsJson.availableCustomers) {
          responseAsJson.availableCustomers = responseAsJson.availableCustomers.sort((a, b) =>
            a.customerName < b.customerName ? -1 : a.customerName > b.customerName ? 1 : 0
          );
          if (responseAsJson.availableCustomers.length === 1) {
            responseAsJson.selectedCustomer = responseAsJson.availableCustomers[0];
          } else if (currentUserState && currentUserState?.RequestedCustomerId) {
            const requestedCustomer = responseAsJson.availableCustomers.find(
              (c) => c.customerId.toString() === currentUserState.RequestedCustomerId
            );
            responseAsJson.selectedCustomer = requestedCustomer;
          }
        }
        return responseAsJson;
      })
      .catch((error: any) => {
        console.warn('Could not fetch user details');
        throw error;
      });
  };

  const updateGlobalLoginState = (userInRole: IUserInRole) => {
    userInRole.selectedCustomer =
      currentUserState?.UserInRole?.selectedCustomer || userInRole.selectedCustomer;
    actionCreators.setLoginState &&
      actionCreators.setLoginState({
        LoginState: AuthenticationStore.LoginState.LOGGED_IN,
        UserInRole: userInRole,
      });
  };

  // let nonMFAAccessToken: string = '';
  function refreshNativeToken() {
    if (refreshTokenPromise == null) {
      refreshTokenPromise = new Promise<string>((resolve, reject) => {
        fetch(APIBaseUrl + '/v1/login/AccessToken/Refresh', {
          method: 'POST',
          mode: 'cors', // no-cors, *cors, same-origin
          cache: 'no-cache',
          credentials: 'same-origin', // include, *same-origin, omit
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'bearer ' + currentUserState?.RefreshToken,
          },
          redirect: 'error',
          referrerPolicy: 'no-referrer',
        })
          .then((response: Response) => {
            refreshTokenPromise = null;
            if (response.status >= 200 && response.status < 300) {
              response.json().then((result: IRefreshedAccessTokenResult) => {
                var tokenData: any = jwt_decode(result.accessToken);
                var refreshedState = {
                  AccessToken: result.accessToken,
                  AccessTokenExpirationDate: new Date(tokenData['exp'] * 1000),
                  MfaWasUsed: false,
                };
                actionCreators.setLoginState && actionCreators.setLoginState(refreshedState);
                resolve(result.accessToken);
              });
            } else {
              if (response.status === 400) {
                reject(new AuthError('400', 'Bad request'));
              } else if (response.status === 401) {
                reject(new AuthError('401', 'Unauthorized'));
              } else if (response.status === 403) {
                reject(new AuthError('403', 'Forbidden'));
              } else {
                reject(new AuthError(response.status.toString(), 'Server error'));
              }
            }
          })
          .catch((error: any) => {
            refreshTokenPromise = null;
            if (error.message && error.message === 'Failed to fetch') {
              reject(new AuthError('-1', 'Could not find server'));
            } else {
              reject({ errorCode: -1, errorMessage: 'Unexpected Error' });
            }
          });
      });
    }
    return refreshTokenPromise;
  }

  function getOrRenewNativeToken(): Promise<string> {
    var _nativeAccessToken: string = '';

    return new Promise((resolve, reject) => {
      console.debug('Getting token from cache.');

      if (
        currentUserState &&
        currentUserState.AccessTokenExpirationDate &&
        currentUserState.AccessTokenExpirationDate > new Date() &&
        currentUserState.AccessToken
      ) {
        console.debug('Using cached access token');
        resolve(currentUserState.AccessToken);
        return;
      }

      if (
        !currentUserState ||
        !currentUserState.RefreshToken ||
        !currentUserState.RefreshTokenExpirationDate ||
        currentUserState.RefreshTokenExpirationDate < new Date()
      ) {
        console.debug('Both cached access token and refresh token was outdated or missing.');
        logoutUser().then(() => {
          throw 'AccessToken_Expired';
        });
        reject();
        return;
      }

      console.debug(
        'Cached token was outdated, using valid refresh token to refresh access token.',
        currentUserState.AccessTokenExpirationDate
      );
      refreshNativeToken()
        .then((nativeAccessToken: string) => {
          console.debug('Access token refresh succeeded');
          var promise = fetchUserDetails(nativeAccessToken);
          promise
            .then(() => {
              var tokenData: any = jwt_decode(nativeAccessToken);
              actionCreators.setLoginState &&
                actionCreators.setLoginState({
                  AccessToken: nativeAccessToken,
                  AccessTokenExpirationDate: new Date(tokenData['exp'] * 1000),
                });
              _nativeAccessToken = nativeAccessToken;
              resolve(_nativeAccessToken);
            })
            .catch((error: any) => {
              console.debug('Could not fetch user details. Error: ' + JSON.stringify(error), error);
              actionCreators.setLoginState &&
                actionCreators.setLoginState({
                  LoginState: AuthenticationStore.LoginState.LOGIN_FAILED,
                  UserInRole: undefined,
                  AccessToken: '',
                });
            });
          return promise;
        })
        .then((userInRole: IUserInRole) => {
          console.debug('New user details were fetched.', userInRole);
          userInRole.selectedCustomer = currentUserState?.UserInRole?.selectedCustomer;
          actionCreators.setLoginState &&
            actionCreators.setLoginState({
              LoginState: AuthenticationStore.LoginState.LOGGED_IN,
              UserInRole: userInRole,
            });
          resolve(_nativeAccessToken);
        });
    });
  }

  function logoutUser() {
    return new Promise<void>((resolve, reject) => {
      actionCreators.setLoginState &&
        actionCreators.setLoginState({
          LoginState: AuthenticationStore.LoginState.NOT_LOGGED_IN,
          UserInRole: undefined,
          AccessToken: '',
          RefreshToken: '',
          AccessTokenExpirationDate: new Date(Date.parse('1970-01-02')),
          RefreshTokenExpirationDate: new Date(Date.parse('1970-01-02')),
          MfaWasUsed: false,
        });
      resolve();
    });
  }

  return {
    GetLimitedUserInformation: function (email: string): Promise<ILimitedUserInformation> {
      return fetch(APIBaseUrl + '/login/customauthentication/getlimiteduserinfo', {
        method: 'Post',
        body: JSON.stringify({ email }),
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        redirect: 'error',
        referrerPolicy: 'no-referrer',
      })
        .then(HandleGetLimitedUserInformationResponse)
        .then((response) => response.json() as Promise<ILimitedUserInformation>)
        .catch(CatchAndThrowFailedToGetInfo);
    },
    RegisterNewUser: (email: string, password: string): Promise<ILimitedUserInformation> => {
      return fetch(APIBaseUrl + '/login/CustomAuthentication/CreateNewUser', {
        body: JSON.stringify({ email: email, password: password }),
        method: 'Post',
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache',
        credentials: 'same-origin', // include, *same-origin, omit
        headers: {
          'Content-Type': 'application/json',
        },
        redirect: 'error',
        referrerPolicy: 'no-referrer',
      })
        .then(HandleCreateNewUserResponse)
        .then((response: Response) => response.json() as Promise<ILimitedUserInformation>)
        .catch(CatchAndThrowFailedToGetInfo);
    },
    // VerifyToken: function (username: string): Promise<boolean> {
    //     return Promise.resolve(true);
    // },
    RequestResetPasswordToken: function (email: string): Promise<void> {
      return fetch(
        APIBaseUrl +
          '/login/CustomAuthentication/RequestResetPasswordToken?email=' +
          encodeURIComponent(email),
        {
          method: 'Get',
          mode: 'cors', // no-cors, *cors, same-origin
          cache: 'no-cache',
          credentials: 'same-origin', // include, *same-origin, omit
          headers: {
            'Content-Type': 'application/json',
          },
          redirect: 'error',
          referrerPolicy: 'no-referrer',
        }
      )
        .then(ThrowAuthErrorOnErrorResponse)
        .then(DoNothing)
        .catch(CatchAndThrowFailedToGetInfo);
    },
    RequestNewVerificationToken: function (email: string): Promise<void> {
      return fetch(APIBaseUrl + '/login/CustomAuthentication/RequestNewVerificationToken', {
        body: JSON.stringify({ email: email }),
        method: 'Post',
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache',
        credentials: 'same-origin', // include, *same-origin, omit
        headers: {
          'Content-Type': 'application/json',
        },
        redirect: 'error',
        referrerPolicy: 'no-referrer',
      })
        .then(ThrowAuthErrorOnErrorResponse)
        .then(() => {})
        .catch(CatchAndThrowFailedToGetInfo);
    },
    FetchUserDetails: function (accessToken: string) {
      return fetchUserDetails(accessToken)
        .then(updateGlobalLoginState)
        .catch((error: any) => {
          console.debug('Could not fetch user details. Error: ' + JSON.stringify(error), error);

          actionCreators.setLoginState &&
            actionCreators.setLoginState({
              LoginState: AuthenticationStore.LoginState.NOT_LOGGED_IN,
              UserInRole: undefined,
              AccessToken: '',
              RefreshToken: '',
              AccessTokenExpirationDate: new Date(Date.parse('1970-01-02')),
              RefreshTokenExpirationDate: new Date(Date.parse('1970-01-02')),
              MfaWasUsed: false,
            });

          if (error.errorCode && error.errorMessage) {
            throw error;
          }
          if (error.message && error.message === 'Failed to get info') {
            throw error;
          } else {
            throw new AuthError('-1', error);
          }
        });
    },
    ResetPassword: (password: string, token: string): Promise<ILimitedUserInformation> => {
      return fetch(APIBaseUrl + '/login/CustomAuthentication/ResetPassword', {
        method: 'Post',
        body: JSON.stringify({ password: password, token: token }),
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache',
        credentials: 'same-origin', // include, *same-origin, omit
        headers: {
          'Content-Type': 'application/json',
        },
        redirect: 'error',
        referrerPolicy: 'no-referrer',
      })
        .then(ThrowApiErrorOnErrorResponse)
        .then((response) => response.json() as Promise<ILimitedUserInformation>)
        .catch(CatchAndThrowFailedToGetInfo);
    },
    SetAuthState: async (accessToken: string, refreshToken: string) => {
      const tokenData: any = jwt_decode(accessToken);
      const refreshTokenData: any = jwt_decode(refreshToken);
      actionCreators.setLoginState &&
        actionCreators.setLoginState({
          AccessToken: accessToken,
          AccessTokenExpirationDate: new Date(tokenData['exp'] * 1000),
          RefreshToken: refreshToken,
          RefreshTokenExpirationDate: new Date(refreshTokenData['exp'] * 1000),
          MfaWasUsed: true,
        });
      const userInRole = await fetchUserDetails(accessToken);
      updateGlobalLoginState(userInRole);
    },
  };
};

export interface ILimitedUserInformation {
  email?: string;
  state: UserRegistrationState;
  token?: string;
  authSetupKey?: string;
  sessionId?: string;
  ssoInfo?: SsoInfo;
}

export enum UserRegistrationState {
  'Unknown',
  'Unregistered',
  'Unverified',
  'Verified',
  'AwaitingOneTimeCode',
  'Authenticated',
  'CustomerNotMigrated',
  'CustomerMigrationNotFinished',
}

export interface ICustomLoginService {
  GetLimitedUserInformation: (email: string) => Promise<ILimitedUserInformation>;
  RegisterNewUser: (username: string, password: string) => Promise<ILimitedUserInformation>;
  RequestNewVerificationToken: (username: string) => Promise<void>;
  RequestResetPasswordToken: (username: string) => Promise<void>;
  ResetPassword: (pw: string, resetPasswordToken: string) => Promise<ILimitedUserInformation>;
  FetchUserDetails: (nativeAccessToken: string) => Promise<void>;
  SetAuthState(accessToken: string, refreshToken: string): Promise<void>;
}

export interface UserRegistrationStateError {
  state: UserRegistrationState;
}

export interface LoginToken {
  accessToken: string;
  refreshToken: string;
}

export default { Create };
